/**
 * PRP-WEB-2 W2.7: the picture under the plan (a Matterport or plan image), pure math. The image
 * is drawn with its top-left at `offset` (plan feet), `scale_ft_per_px` feet per image pixel,
 * turned `rotation` degrees about its top-left. Calibrate: a line drawn over one known
 * dimension rescales the image about the line's first end, which stays put.
 */
import { dist, planBounds, type Plan, type Pt } from '@sketch-core';
import type { PlanUnderlay } from '../../../../lib/planApi';

export interface UnderlayState extends PlanUnderlay {
  /** What the browser loads: a signed url from the server, or a local object url right after a pick. */
  url: string;
  /** The image's size in pixels, once loaded. */
  natW: number;
  natH: number;
}

export const DEFAULT_OPACITY = 0.5;

/** A new picture: as wide as the plan, its top-left on the plan's (or the origin when the plan is empty). */
export function placeNew(plan: Plan, natW: number): Pick<PlanUnderlay, 'scale_ft_per_px' | 'offset' | 'rotation' | 'opacity'> {
  const b = planBounds(plan);
  const w = b ? Math.max(b[2] - b[0], 10) : 40;
  return { scale_ft_per_px: w / Math.max(1, natW), offset: { x: b ? b[0] : 0, y: b ? b[1] : 0 }, rotation: 0, opacity: DEFAULT_OPACITY };
}

/** Rescale so the drawn line `a`-`b` measures `knownFt`; `a` stays where it is. Null when the line is too short. */
export function calibrate(u: Pick<PlanUnderlay, 'scale_ft_per_px' | 'offset'>, a: Pt, b: Pt, knownFt: number): Pick<PlanUnderlay, 'scale_ft_per_px' | 'offset'> | null {
  const d = dist(a, b);
  if (d < 1e-6 || !(knownFt > 0)) return null;
  const k = knownFt / d;
  return { scale_ft_per_px: u.scale_ft_per_px * k, offset: { x: a.x + (u.offset.x - a.x) * k, y: a.y + (u.offset.y - a.y) * k } };
}

/** Move by a plan-feet delta (drag to align). */
export const moveBy = (u: Pick<PlanUnderlay, 'offset'>, dx: number, dy: number) => ({ offset: { x: u.offset.x + dx, y: u.offset.y + dy } });

/** What is saved (the url is the server's to sign; never sent back). */
export function toSaved(u: UnderlayState): PlanUnderlay {
  return { path: u.path, scale_ft_per_px: u.scale_ft_per_px, offset: { ...u.offset }, rotation: u.rotation, opacity: u.opacity };
}
